import { match, P } from 'ts-pattern';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { pipe } from 'fp-ts/function';
import { differenceInDays, isValid, startOfDay, subDays } from 'date-fns';
import { BrandAffinityLevel } from 'modules/domains';
import { some, none, Option, isNone, getOrElse, map, ap } from 'fp-ts/Option';
import {
  getOtherBrandAffinityCohorts,
  formatAsLocalDate,
  trimNumber,
} from 'modules/intelligentInsights/domain.utils';
import { parseDate } from 'utils/datetime';
import { sentenceCase } from 'utils/stringHelpers';
import {
  AllUnscoredMetrics,
  BrandAffinityInsightData,
  BrandAffinityInsightsDataManagementContext,
  BrandAffinityLevelDeletedUser,
  BrandAffinityLevelNewUser,
  CalculatedDeltas,
  CalculatedEngagementMetrics,
  CalculatedTopCampaign,
  CalculatedUsersMetrics,
  CampaignImpact,
  CampaignImpactAffinityLevels,
  CampaignImpactChartData,
  CampaignImpactDataCreators,
  CampaignImpactItemWrapper,
  ChannelCampaignImpact,
  Count,
  DeltaDirectionEnum,
  DeltaItem,
  Deltas,
  DirectionEnum,
  EngagementMetricEnum,
  EngagementMetricItem,
  EngagementMetrics,
  EngagementOverview,
  GraphHistory,
  History,
  HistoryLabel,
  Metrics,
  MovementByCohortMetrics,
  SupportedMediums,
  Timespan,
  TransitionMap,
  TrendTypeEnum,
  UnscoredMetrics,
} from 'modules/intelligentInsights/brandAffinityInsights/types';
import {
  defaultCalculatedDeltas,
  defaultDataManagementContext,
  defaultUnscored,
} from 'modules/intelligentInsights/brandAffinityInsights/contexts/dataManagement/DataManagementContext';
import { isDefined } from 'utils/tsHelpers';
import { MESSAGE_MEDIUMS, MessageMedium } from 'constants/messageMediums';
import {
  AFFINITY_TREND_CSV_DIRECTIONS,
  BRAND_AFFINITY_CYCLE_DAYS,
  MOVEMENT_DELETED_USERS,
  MOVEMENT_NEW_TO_PROJECT,
} from 'modules/intelligentInsights/brandAffinityInsights/constants';

type LabelWeight = { label: BrandAffinityLevel; weight: number };
export const BrandLabelWeights: Array<LabelWeight> = [
  { label: BrandAffinityLevel.LOYAL, weight: 5 },
  { label: BrandAffinityLevel.POSITIVE, weight: 4 },
  { label: BrandAffinityLevel.NEUTRAL, weight: 3 },
  { label: BrandAffinityLevel.NEGATIVE, weight: 2 },
  { label: BrandAffinityLevel.UNSCORED, weight: 1 },
];

type SerializedBAInsightsData = Record<keyof BrandAffinityInsightData, unknown>;

export function sortByLabelWeight<LabelT extends { brandAffinityLabel: BrandAffinityLevel }>(
  labelA: LabelT,
  labelB: LabelT,
) {
  return (
    (BrandLabelWeights.find((item) => item.label === labelB.brandAffinityLabel) as LabelWeight)
      .weight -
    (BrandLabelWeights.find((item) => item.label === labelA.brandAffinityLabel) as LabelWeight)
      .weight
  );
}

export const scoreSort = (a: CalculatedTopCampaign, b: CalculatedTopCampaign) => b.score - a.score;

export type HasBrandAffinity = {
  brandAffinityLabel: BrandAffinityLevel;
};
export function ensureCompleteLabelArray<TLabelItem extends HasBrandAffinity>(
  defaultProperties: Record<string, any>,
  labelCounts: TLabelItem[] | null,
) {
  return Object.values(BrandAffinityLevel).map((level) => {
    return (
      labelCounts?.find((label) => label.brandAffinityLabel === level) ||
      ({
        brandAffinityLabel: level,
        ...defaultProperties,
      } as TLabelItem)
    );
  });
}

export const calculateOneCycleFromDate = (date: Date, dates: string[], cyclesToCalculate: number) =>
  [subDays(date, BRAND_AFFINITY_CYCLE_DAYS * cyclesToCalculate).toISOString()].concat(dates);

export const calculateCyclesFromDate = (
  date: Date,
  cyclesRemaining: number,
  cyclesToCalculate = 1,
  dates = [] as string[],
): string[] => {
  if (cyclesRemaining <= 0) return dates;
  return calculateCyclesFromDate(
    date,
    cyclesRemaining - 1,
    cyclesToCalculate + 1,
    calculateOneCycleFromDate(date, dates, cyclesToCalculate),
  );
};

export const calculateMissingHistoryDates = (
  earliestComputedDate: string,
  selectedTimespan: Timespan,
  compareDate = new Date(),
) => {
  const parsedComputedDate = parseDate(earliestComputedDate);
  if (!isValid(parsedComputedDate) || !selectedTimespan) return { remainingDates: [] };

  const parsedEarliestDate = startOfDay(parsedComputedDate);
  const requestedDate = subDays(startOfDay(parseDate(compareDate)), selectedTimespan);
  const daysDiff = differenceInDays(parsedEarliestDate, requestedDate);
  const numberOfCycles = Math.floor(daysDiff / 7);
  const remainingCycleDates = calculateCyclesFromDate(parsedEarliestDate, numberOfCycles);
  return {
    remainingDates: remainingCycleDates,
  };
};

export const CSVHistoryDataKeys = [
  'Date',
  sentenceCase(BrandAffinityLevel.UNSCORED),
  sentenceCase(BrandAffinityLevel.NEGATIVE),
  sentenceCase(BrandAffinityLevel.NEUTRAL),
  sentenceCase(BrandAffinityLevel.POSITIVE),
  sentenceCase(BrandAffinityLevel.LOYAL),
];
export const historyCSVDataCreator =
  (history: Option<GraphHistory>) => (trendType: TrendTypeEnum) => {
    const generatedCSVData = map((graphHistory: GraphHistory) => {
      const computedHeaders = CSVHistoryDataKeys.map((datakey) => ({
        key: datakey,
        label: datakey,
      }));

      const computedData = graphHistory.categories.map((category, categoryIndex) => {
        const [loyal, positive, neutral, negative, unscored] =
          trendType === TrendTypeEnum.Count
            ? graphHistory.byCount.historyPoints
            : graphHistory.byPercent.historyPoints;
        return {
          [CSVHistoryDataKeys[0]]: formatAsLocalDate(category),
          [CSVHistoryDataKeys[1]]: unscored[categoryIndex],
          [CSVHistoryDataKeys[2]]: negative[categoryIndex],
          [CSVHistoryDataKeys[3]]: neutral[categoryIndex],
          [CSVHistoryDataKeys[4]]: positive[categoryIndex],
          [CSVHistoryDataKeys[5]]: loyal[categoryIndex],
        };
      });

      return {
        headers: computedHeaders,
        csvData: computedData,
      };
    })(history);
    const { headers, csvData } = getOrElse(() =>
      defaultDataManagementContext.getHistoryCSVData(TrendTypeEnum.Percent),
    )(generatedCSVData);
    return {
      headers,
      csvData,
    };
  };

export const CSVEngagementHeaderKeys = [
  'Current Brand Affinity Label',
  'User Count',
  'Sends',
  'Opens',
  'Clicks',
];
export const CSVEngagementDataKeys = [
  BrandAffinityLevel.LOYAL,
  BrandAffinityLevel.POSITIVE,
  BrandAffinityLevel.NEUTRAL,
  BrandAffinityLevel.NEGATIVE,
];
export const getEngagementData = (
  parsedEngagementData: Option<CalculatedEngagementMetrics>,
  parsedUsersMetrics: Option<CalculatedUsersMetrics>,
) => {
  const innerGenerateCSVData =
    (engagementData: CalculatedEngagementMetrics) => (usersMetrics: CalculatedUsersMetrics) => {
      const headers = CSVEngagementHeaderKeys.map((datakey) => ({ key: datakey, label: datakey }));
      const csvData = CSVEngagementDataKeys.map((level) => {
        return {
          [CSVEngagementHeaderKeys[0]]: sentenceCase(level),
          [CSVEngagementHeaderKeys[1]]: usersMetrics[level].history.count,
          [CSVEngagementHeaderKeys[2]]: engagementData[level].overview[EngagementMetricEnum.Sends],
          [CSVEngagementHeaderKeys[3]]: engagementData[level].overview[EngagementMetricEnum.Opens],
          [CSVEngagementHeaderKeys[4]]: engagementData[level].overview[EngagementMetricEnum.Clicks],
        };
      });
      return {
        headers,
        csvData,
      };
    };

  const generatedCSVData = pipe(
    some(innerGenerateCSVData),
    ap(parsedEngagementData),
    ap(parsedUsersMetrics),
  );
  return getOrElse(() => defaultDataManagementContext.affinityBreakdownCSVData.engagementData)(
    generatedCSVData,
  );
};

export const CSVMovementHeaderKeys = [
  'Current Brand Affinity Label',
  'Previous Brand Affinity Label',
  'Number of Users',
];
export const getMovementData = (parsedMovementData: Option<MovementByCohortMetrics[]>) => {
  const generatedCSVData = map((movementData: MovementByCohortMetrics[]) => {
    const headers = CSVMovementHeaderKeys.map((datakey) => ({ key: datakey, label: datakey }));

    const csvData = movementData.flatMap(
      ({ cohort, newUsersCohort, newUsersToProject, totalRetainedUsers, deletedUsers }) => {
        const movedToCohortData = Object.values(BrandLabelWeights).map((entry) => {
          const count =
            entry.label === cohort ? totalRetainedUsers : newUsersCohort[entry.label] || 0;
          return {
            [CSVMovementHeaderKeys[0]]: sentenceCase(cohort),
            [CSVMovementHeaderKeys[1]]: sentenceCase(entry.label),
            [CSVMovementHeaderKeys[2]]: count,
          };
        });
        movedToCohortData.push({
          [CSVMovementHeaderKeys[0]]: sentenceCase(cohort),
          [CSVMovementHeaderKeys[1]]: MOVEMENT_NEW_TO_PROJECT,
          [CSVMovementHeaderKeys[2]]: newUsersToProject,
        });
        movedToCohortData.push({
          [CSVMovementHeaderKeys[0]]: MOVEMENT_DELETED_USERS,
          [CSVMovementHeaderKeys[1]]: sentenceCase(cohort),
          [CSVMovementHeaderKeys[2]]: deletedUsers,
        });
        return movedToCohortData;
      },
    );
    return {
      csvData,
      headers,
    };
  })(parsedMovementData);

  return getOrElse(() => defaultDataManagementContext.affinityBreakdownCSVData.movementData)(
    generatedCSVData,
  );
};

export const CSVCampaignHeaderKeys = [
  'Current Brand Affinity Label',
  'Campaign ID',
  'Channel',
  'Contribution Direction',
];
// Remove unscored
const includeCampaignLabels = BrandLabelWeights.slice(0, -1);
export const getCampaignsData = (parsedCampaignImpacts: Option<CampaignImpactDataCreators>) => {
  const generatedCSVData = map((creators: CampaignImpactDataCreators) => {
    const headers = CSVCampaignHeaderKeys.map((datakey) => ({ key: datakey, label: datakey }));

    const { getSortedCampaigns } = creators;

    const csvData = Object.values(includeCampaignLabels).flatMap((entry) => {
      const { up, down } = getSortedCampaigns(entry.label as CampaignImpactAffinityLevels);
      const allCampaigns = up.concat(down).sort(scoreSort);
      return allCampaigns.map((campaign) => {
        return {
          [CSVCampaignHeaderKeys[0]]: sentenceCase(entry.label),
          [CSVCampaignHeaderKeys[1]]: campaign.campaignId,
          [CSVCampaignHeaderKeys[2]]: campaign.messageMedium,
          [CSVCampaignHeaderKeys[3]]:
            AFFINITY_TREND_CSV_DIRECTIONS[
              campaign.direction as DirectionEnum.up | DirectionEnum.down
            ],
        };
      });
    });

    return {
      headers,
      csvData,
    };
  })(parsedCampaignImpacts);
  return getOrElse(() => defaultDataManagementContext.affinityBreakdownCSVData.campaigns)(
    generatedCSVData,
  );
};

export const getAffinityBreakdownCSVData = (
  parsedInsightsData: BrandAffinityInsightsDataManagementContext['managedInsightsData'],
) => {
  return {
    engagementData: getEngagementData(
      parsedInsightsData.engagementMetrics,
      parsedInsightsData.usersMetrics,
    ),
    movementData: getMovementData(parsedInsightsData.calculatedMovementMetrics),
    campaigns: getCampaignsData(parsedInsightsData.campaignImpact),
  };
};

/**
 History
 */
export const parseBrandHistory = (
  serializedBAInsightsData: SerializedBAInsightsData,
): Option<GraphHistory> => {
  if (isEmpty(serializedBAInsightsData.history)) {
    return none;
  }

  const brandAffinityCount = 5;

  const categories = [] as string[];
  const historyByCounts = new Array(brandAffinityCount).fill(null).map(() => []) as Array<Count[]>;
  const historyByPercents = new Array(brandAffinityCount).fill(null).map(() => []) as Array<
    Count[]
  >;
  (serializedBAInsightsData.history as History).forEach((historyItem) => {
    categories.push(historyItem.computedForDate);

    const { sortedLabels } = ensureCompleteLabelArray<HistoryLabel>(
      { count: 0, percent: 0 },
      historyItem.labelCounts,
    )
      .concat()
      .sort(sortByLabelWeight)
      .reduce(
        (accLabels, labelItem) => {
          return {
            sortedLabels: accLabels.sortedLabels.concat(labelItem),
          };
        },
        { sortedLabels: [] as HistoryLabel[] },
      );

    sortedLabels.forEach((labelItem, index) => {
      historyByCounts[index].push(labelItem.count);
      historyByPercents[index].push(trimNumber(labelItem.percent));
    });
  });

  return some({
    categories,
    byCount: {
      historyPoints: historyByCounts,
    },
    byPercent: {
      historyPoints: historyByPercents,
    },
  });
};

/**
 Deltas
*/

export const parseDeltas = (
  serializedBAInsightsData: SerializedBAInsightsData,
): Option<CalculatedDeltas> => {
  let unscored = defaultUnscored;
  if (!isEmpty(serializedBAInsightsData.history)) {
    const history = serializedBAInsightsData.history as History;
    const recentHistoryEntry = history[history.length - 1];
    const { count, percent, brandAffinityLabel } = recentHistoryEntry.labelCounts.find(
      (entry) => entry.brandAffinityLabel === BrandAffinityLevel.UNSCORED,
    ) || {
      count: 0,
      brandAffinityLabel: BrandAffinityLevel.UNSCORED,
      percent: 0,
    };
    unscored = {
      brandAffinityLabel,
      delta: count,
      percent: trimNumber(percent),
    };
  }

  if (isEmpty(serializedBAInsightsData.deltas)) {
    return some({
      byCount: {
        ...defaultCalculatedDeltas.byCount,
        [DeltaDirectionEnum.unscored]: unscored,
      },
      byPercent: {
        ...defaultCalculatedDeltas.byPercent,
        [DeltaDirectionEnum.unscored]: unscored,
      },
    });
  }

  const completeDeltas = ensureCompleteLabelArray<DeltaItem>(
    { delta: 0, percent: 0 },
    serializedBAInsightsData.deltas as Deltas,
  );
  const sortedWeightedDeltas = completeDeltas
    .concat()
    .sort(sortByLabelWeight)
    .reduce(
      (acc, currentDeltaEntry) => {
        const nextCountGain =
          acc.byCount.gain.delta >= currentDeltaEntry.delta ? acc.byCount.gain : currentDeltaEntry;
        const nextCountDecline =
          acc.byCount.decline.delta <= currentDeltaEntry.delta
            ? acc.byCount.decline
            : currentDeltaEntry;
        const nextPercentGain =
          trimNumber(acc.byPercent.gain.percent) >= trimNumber(currentDeltaEntry.percent)
            ? acc.byPercent.gain
            : currentDeltaEntry;
        const nextPercentDecline =
          trimNumber(acc.byPercent.decline.percent) <= trimNumber(currentDeltaEntry.percent)
            ? acc.byPercent.decline
            : currentDeltaEntry;
        return {
          byCount: {
            gain: {
              ...nextCountGain,
              percent: trimNumber(nextPercentGain.percent),
            },
            decline: {
              ...nextCountDecline,
              percent: trimNumber(nextPercentGain.percent),
            },
          },
          byPercent: {
            gain: {
              ...nextPercentGain,
              percent: trimNumber(nextPercentGain.percent),
            },
            decline: {
              ...nextPercentDecline,
              percent: trimNumber(nextPercentDecline.percent),
            },
          },
        };
      },
      {
        byCount: {
          gain: {
            brandAffinityLabel: BrandAffinityLevel.UNSCORED,
            delta: -Infinity,
            percent: 0,
          },
          decline: {
            brandAffinityLabel: BrandAffinityLevel.UNSCORED,
            delta: Infinity,
            percent: 0,
          },
        },
        byPercent: {
          gain: {
            brandAffinityLabel: BrandAffinityLevel.UNSCORED,
            delta: 0,
            percent: -Infinity,
          },
          decline: {
            brandAffinityLabel: BrandAffinityLevel.UNSCORED,
            delta: 0,
            percent: Infinity,
          },
        },
      },
    );

  const { byCount, byPercent } = sortedWeightedDeltas;

  return some({
    byCount: {
      ...byCount,
      [DeltaDirectionEnum.unscored]: unscored,
    },
    byPercent: {
      ...byPercent,
      [DeltaDirectionEnum.unscored]: unscored,
    },
  });
};

/**
  Engagement Metrics
 */
const defaultMetricsByMedium = SupportedMediums.map((medium) => ({
  messageMedium: medium,
  metrics: [] as Array<EngagementMetricItem>,
}));
const createDefaultMetricsByLevel = (brandAffinitylevel: BrandAffinityLevel) => ({
  brandAffinityLabel: brandAffinitylevel,
  metrics: defaultMetricsByMedium,
});
const calculateEngagementMetric = (
  metricEnum: EngagementMetricEnum,
  accum: EngagementOverview,
  count: number,
) => ({
  ...accum,
  [metricEnum]: accum[metricEnum] + count,
});
export const parseEngagementMetrics = (
  serializedBAInsightsData: SerializedBAInsightsData,
): Option<CalculatedEngagementMetrics> => {
  const rawEngagementMetrics = serializedBAInsightsData.engagementMetrics;
  if (isEmpty(rawEngagementMetrics)) {
    return none;
  }

  const finalResult: CalculatedEngagementMetrics = Object.values(BrandAffinityLevel).reduce(
    (dataByLevels, level) => {
      if (level === BrandAffinityLevel.UNSCORED) {
        return {
          ...dataByLevels,
          [level]: {
            overview: {
              [EngagementMetricEnum.Clicks]: 0,
              [EngagementMetricEnum.Opens]: 0,
              [EngagementMetricEnum.Sends]: 0,
            },
          },
        };
      }

      const parsedEngagementMetric = (rawEngagementMetrics as EngagementMetrics).find(
        (allMediumMetrics) => allMediumMetrics.brandAffinityLabel === level,
      );
      const allMetricsByLevel =
        parsedEngagementMetric === undefined || parsedEngagementMetric.metrics === null
          ? createDefaultMetricsByLevel(level)
          : parsedEngagementMetric;
      const allEngagementMetrics = (allMetricsByLevel.metrics as Metrics).flatMap(
        (mediumMetrics) => {
          return mediumMetrics.metrics.map((innerMetrics) => innerMetrics);
        },
      );

      const levelResult: EngagementOverview = allEngagementMetrics.reduce(
        (accum, currentMetric) => {
          return match(currentMetric.metricLabel)
            .with(EngagementMetricEnum.Clicks, () =>
              calculateEngagementMetric(EngagementMetricEnum.Clicks, accum, currentMetric.count),
            )
            .with(EngagementMetricEnum.Opens, () =>
              calculateEngagementMetric(EngagementMetricEnum.Opens, accum, currentMetric.count),
            )
            .with(EngagementMetricEnum.Sends, () =>
              calculateEngagementMetric(EngagementMetricEnum.Sends, accum, currentMetric.count),
            )
            .otherwise(() => accum);
        },
        {
          [EngagementMetricEnum.Clicks]: 0,
          [EngagementMetricEnum.Opens]: 0,
          [EngagementMetricEnum.Sends]: 0,
        },
      );

      return {
        ...dataByLevels,
        [level]: {
          overview: levelResult,
        },
      };
    },
    {} as CalculatedEngagementMetrics,
  );

  return some(finalResult);
};

/**
 Movement Metrics
 */
const getDefaultAcc = (cohort: BrandAffinityLevel) => {
  const otherBACohorts = getOtherBrandAffinityCohorts(cohort);

  const defaultAcc: MovementByCohortMetrics = {
    cohort,
    deletedUsers: 0,
    newUsersCohort: {},
    newUsersToProject: 0,
    totalNewUsers: 0,
    totalRetainedUsers: 0,
    totalUsersLeft: 0,
    usersLeftCohort: {},
  };

  otherBACohorts.forEach((c) => {
    defaultAcc.usersLeftCohort[c] = 0;
    defaultAcc.newUsersCohort[c] = 0;
  });
  return defaultAcc;
};
const getCalculationsForCohort = (cohort: BrandAffinityLevel, transitionMap: TransitionMap) => {
  const defaultAcc = getDefaultAcc(cohort);
  const calculationsForCohort = transitionMap.reduce((acc, item) => {
    return match(item)
      .with(
        {
          startBrandAffinityLabel: cohort,
          endBrandAffinityLabel: P.not(cohort),
        },
        () => {
          if (item.endBrandAffinityLabel === BrandAffinityLevelDeletedUser) {
            acc.deletedUsers = item.count;
          } else {
            acc.usersLeftCohort[item.endBrandAffinityLabel]! += item.count;
          }
          acc.totalUsersLeft += item.count;
          return acc;
        },
      )
      .with(
        {
          startBrandAffinityLabel: cohort,
          endBrandAffinityLabel: cohort,
        },
        () => {
          acc.totalRetainedUsers += item.count;
          return acc;
        },
      )
      .with(
        {
          startBrandAffinityLabel: P.not(cohort),
          endBrandAffinityLabel: cohort,
        },
        () => {
          if (item.startBrandAffinityLabel === BrandAffinityLevelNewUser) {
            acc.newUsersToProject = item.count;
          } else {
            acc.newUsersCohort[item.startBrandAffinityLabel]! += item.count;
          }
          acc.totalNewUsers += item.count;
          return acc;
        },
      )
      .otherwise(() => acc);
  }, defaultAcc);

  return calculationsForCohort;
};
export const calculateCohortMovementMetrics = (
  transitionMap: TransitionMap,
): Option<MovementByCohortMetrics[]> => {
  const calculated: MovementByCohortMetrics[] = Object.values(BrandAffinityLevel).map((cohort) => {
    return getCalculationsForCohort(cohort, transitionMap);
  });

  return some(calculated);
};

/**
 * Campaign Impacts
 */
export const cohortToDirections = {
  [BrandAffinityLevel.LOYAL]: [DirectionEnum.up, DirectionEnum.unchanged],
  [BrandAffinityLevel.POSITIVE]: [DirectionEnum.up, DirectionEnum.down, DirectionEnum.unchanged],
  [BrandAffinityLevel.NEUTRAL]: [DirectionEnum.up, DirectionEnum.down, DirectionEnum.unchanged],
  [BrandAffinityLevel.NEGATIVE]: [DirectionEnum.down, DirectionEnum.unchanged],
};
export const createCampaignImpactChartData = (
  cohortCampaignData: CampaignImpactItemWrapper,
  chartData: Array<{ name: DirectionEnum; data: number[] }>,
) => {
  return SupportedMediums.reduce(
    (accData, messageMedium) => {
      const countsForMedium = cohortCampaignData.campaignCount?.filter(
        (campaignCountItem) => campaignCountItem.messageMedium === messageMedium,
      );
      if (isEmpty(countsForMedium)) {
        accData.mediums.push(null);
        return accData;
      }

      accData.mediums.push(
        match(messageMedium)
          .with(MessageMedium.Email, () => MESSAGE_MEDIUMS.EMAIL.label)
          .with(MessageMedium.InApp, () => MESSAGE_MEDIUMS.IN_APP.label)
          .with(MessageMedium.Push, () => MESSAGE_MEDIUMS.PUSH.label)
          .with(MessageMedium.SMS, () => MESSAGE_MEDIUMS.SMS.label)
          .otherwise(() => MESSAGE_MEDIUMS.EMAIL.label),
      );
      accData.chartData.forEach((chartDataDirection) => {
        const maybeCounts = (countsForMedium as ChannelCampaignImpact[]).find(
          (counts) => counts.direction === chartDataDirection.name,
        )?.count;
        chartDataDirection.data.push(isDefined(maybeCounts) ? maybeCounts : 0);
      });

      return accData;
    },
    {
      mediums: [] as Array<string | null>,
      chartData,
    },
  );
};
export const getDefaultCampaignImpact = (
  targetAffinityLevel: BrandAffinityLevel,
): CampaignImpactItemWrapper => ({
  brandAffinityLabel: targetAffinityLevel,
  campaignCount: [],
  topCampaignsUpwards: [],
  topCampaignsDownwards: [],
});
export const parseCampaignImpacts = (serializedBAInsightsData: SerializedBAInsightsData) => {
  if (isEmpty(serializedBAInsightsData.campaignImpacts)) {
    return none;
  }

  const campaignImpactByCohort = (
    brandAffinityCohort: CampaignImpactAffinityLevels = BrandAffinityLevel.LOYAL,
  ): CampaignImpactChartData => {
    const cohortCampaignData =
      (serializedBAInsightsData.campaignImpacts as CampaignImpact).find(
        (campaignImpact) => campaignImpact.brandAffinityLabel === brandAffinityCohort,
      ) || getDefaultCampaignImpact(brandAffinityCohort);
    const directions = cohortToDirections[brandAffinityCohort];
    const chartData = directions.map((direction) => ({ name: direction, data: [] as number[] }));
    return createCampaignImpactChartData(cohortCampaignData, chartData);
  };

  const sliceCount = 5;
  const topCampaignsByCohort = (
    brandAffinityCohort: CampaignImpactAffinityLevels = BrandAffinityLevel.LOYAL,
  ) => {
    const cohortCampaignData =
      (serializedBAInsightsData.campaignImpacts as CampaignImpact).find(
        (campaignImpact) => campaignImpact.brandAffinityLabel === brandAffinityCohort,
      ) || getDefaultCampaignImpact(brandAffinityCohort);
    const upCampaigns =
      cohortCampaignData.topCampaignsUpwards?.map((campaign) => ({
        ...campaign,
        direction: DirectionEnum.up,
      })) || [];
    const downCampaigns =
      cohortCampaignData.topCampaignsDownwards?.map((campaign) => ({
        ...campaign,
        direction: DirectionEnum.down,
      })) || [];

    return {
      all: upCampaigns.concat(downCampaigns).sort(scoreSort).slice(0, sliceCount),
      down: downCampaigns.sort(scoreSort).slice(0, sliceCount),
      up: upCampaigns.sort(scoreSort).slice(0, sliceCount),
    };
  };

  return some({
    getCampaignChartData: campaignImpactByCohort,
    getSortedCampaigns: topCampaignsByCohort,
  });
};

/**
 Users Metrics
 */
export const calculateUserMetrics = (serializedBAInsightsData: SerializedBAInsightsData) => {
  if (isEmpty(serializedBAInsightsData.history)) {
    return none;
  }

  const history: History = serializedBAInsightsData.history as History;

  const recentScoring = history[history.length - 1];
  const earliestScoring = history[0];

  return some(
    Object.values(BrandAffinityLevel).reduce((accMetrics, level) => {
      const recentLevelMetrics = recentScoring.labelCounts?.find(
        (labelCount) => labelCount.brandAffinityLabel === level,
      ) || { count: 0, brandAffinityLabel: level, percent: 0 };

      if (history.length < 2) {
        return {
          ...accMetrics,
          [level]: {
            history: {
              count: recentLevelMetrics.count,
              percent: trimNumber(recentLevelMetrics.percent),
            },
            delta: {
              count: recentLevelMetrics.count,
              percent: trimNumber(recentLevelMetrics.percent),
            },
          },
        };
      }

      const earliestLevelMetrics = earliestScoring.labelCounts?.find(
        (labelCount) => labelCount.brandAffinityLabel === level,
      ) || { count: 0, brandAffinityLabel: level, percent: 0 };
      const countDelta = recentLevelMetrics.count - earliestLevelMetrics.count;
      const percentDiff =
        trimNumber(recentLevelMetrics.percent) - trimNumber(earliestLevelMetrics.percent);
      return {
        ...accMetrics,
        [level]: {
          history: {
            count: recentLevelMetrics.count,
            percent: trimNumber(recentLevelMetrics.percent),
          },
          delta: {
            count: countDelta,
            percent: trimNumber(percentDiff),
          },
        },
      };
    }, {} as CalculatedUsersMetrics),
  );
};

export const parseUnscoredMetrics = (
  serializedBAInsightsData: SerializedBAInsightsData,
  selectedTimespan: Timespan,
) => {
  if (isEmpty(serializedBAInsightsData.unscoredMetrics)) {
    return none;
  }

  let totalSends = 0;
  if (!isEmpty(serializedBAInsightsData.engagementMetrics)) {
    const unscoredMetrics = (serializedBAInsightsData.engagementMetrics as EngagementMetrics).find(
      (dataByLevels) => dataByLevels.brandAffinityLabel === BrandAffinityLevel.UNSCORED,
    ) || { brandAffinityLabel: BrandAffinityLevel.UNSCORED, metrics: [] };

    totalSends =
      unscoredMetrics.metrics?.reduce((metricsByLevelTotal, metricsItem) => {
        return (
          metricsByLevelTotal +
          metricsItem.metrics.reduce((metricsDataByMediumTotal, innerMetricsItem) => {
            if (innerMetricsItem.metricLabel === EngagementMetricEnum.Sends) {
              return metricsDataByMediumTotal + innerMetricsItem.count;
            }
            return metricsDataByMediumTotal;
          }, 0)
        );
      }, 0) || 0;
  }
  const unscoredMetricsByTimespan =
    (serializedBAInsightsData.unscoredMetrics as AllUnscoredMetrics).find(
      (metrics) => metrics.timespan === selectedTimespan,
    ) || ({ totalSendSkips: 0, uniqueUsersUnsub: 0 } as UnscoredMetrics);

  return some({
    ...unscoredMetricsByTimespan,
    topUnsubMessageTypeIds: unscoredMetricsByTimespan.topUnsubMessageTypeIds || [],
    topUnsubChannelIds: unscoredMetricsByTimespan.topUnsubChannelIds || [],
    totalSends,
  });
};

export const parseCoreInsightsData = (
  serializedBAInsightsData: SerializedBAInsightsData,
  selectedTimespan: Timespan,
): BrandAffinityInsightsDataManagementContext['managedInsightsData'] => {
  return {
    history: parseBrandHistory(serializedBAInsightsData),
    deltas: parseDeltas(serializedBAInsightsData),
    usersMetrics: calculateUserMetrics(serializedBAInsightsData),
    engagementMetrics: parseEngagementMetrics(serializedBAInsightsData),
    campaignImpact: parseCampaignImpacts(serializedBAInsightsData),
    unscoredMetrics: parseUnscoredMetrics(serializedBAInsightsData, selectedTimespan),
    calculatedMovementMetrics: calculateCohortMovementMetrics(
      serializedBAInsightsData.transitionMap as TransitionMap,
    ),
  };
};

export const parsedResponseHasNoData = (
  managedInsightsData: BrandAffinityInsightsDataManagementContext['managedInsightsData'],
) => {
  // Intricate default structure for movement metrics
  const { calculatedMovementMetrics, deltas, ...restData } = managedInsightsData;

  const hasNoneValues = Object.values(restData).every(isNone);
  const deltasAreDefault = getOrElse(() => defaultCalculatedDeltas)(deltas);
  return hasNoneValues && isEqual(deltasAreDefault, defaultCalculatedDeltas);
};
